import React from "react";

import { CloseIcon } from "@chakra-ui/icons";
import { Box, HStack, Icon, Text, Tooltip, VStack } from "@chakra-ui/react";
import { PhCalculationResponse, PhCalculationWithHistoryResponse } from "@henophess/types";

interface PhMeasurementsChartProps {
  analysis: PhCalculationResponse | PhCalculationWithHistoryResponse;
}

const MIN_PH = 3;
const MAX_PH = 9;
const OPTIMAL_PH = 6.4;
const LINE_WIDTH = "300px";
const MARKER_SIZE = "12px";
const X_MARKER_SIZE = 5;
const LABEL_WIDTH = "140px";
const LABEL_MARGIN = "20px";
const TICK_HEIGHT = "6px";
const OPTIMAL_LINE_COLOR = "green.500";

const getPhColor = (value: number): string => {
  if (value < 4.8 || value > 8) return "red.500";
  if (value < 6 || value > 6.8) return "orange.400";
  if (value < 6.1 || value > 6.7) return "yellow.400";
  return "green.500";
};

interface PhValueMarkerProps {
  value: number;
  label: string;
  opacity?: number;
}

const PhValueMarker: React.FC<PhValueMarkerProps> = ({ value, label, opacity = 1 }) => {
  const isOutOfRange = value < 3 || value > 10;
  const valuePosition = isOutOfRange ? (value < 3 ? 0 : 100) : ((value - MIN_PH) / (MAX_PH - MIN_PH)) * 100;
  const phColor = getPhColor(value);

  return isOutOfRange ? (
    <Tooltip label={`${label}: ${value.toFixed(1)}`} placement="top">
      <Box position="absolute" top="2px" left={`${valuePosition}%`} transform="translateX(-50%)" opacity={opacity}>
        <Icon as={CloseIcon} w={X_MARKER_SIZE} h={X_MARKER_SIZE} color="red.500" />
      </Box>
    </Tooltip>
  ) : (
    <Tooltip label={`${label}: ${value.toFixed(1)}`} placement="top">
      <Box
        position="absolute"
        top="6px"
        left={`${valuePosition}%`}
        width={MARKER_SIZE}
        height={MARKER_SIZE}
        borderRadius="full"
        bg={phColor}
        transform="translateX(-50%)"
        opacity={opacity}
      />
    </Tooltip>
  );
};

const PhRow: React.FC<{ label: string; currentValue: number; historicalValues?: number[] }> = ({
  label,
  currentValue,
  historicalValues = []
}) => {
  const displayValue = typeof currentValue === "number" ? Number(currentValue).toFixed(1) : "-";

  return (
    <HStack spacing={LABEL_MARGIN} width="100%" align="center">
      <Text width={LABEL_WIDTH} fontSize="sm" fontWeight="medium">
        {label}
      </Text>
      <Box position="relative" width={LINE_WIDTH} height="24px">
        <Box position="absolute" top="11px" left="0" right="0" height="2px" bg="gray.300" />

        {/* Historical markers with reduced opacity */}
        {historicalValues.map((value, index) => (
          <PhValueMarker key={`historical-${index}`} value={value} label={`Week ${index + 1} ${label}`} opacity={0.3} />
        ))}

        {/* Current value marker */}
        <PhValueMarker value={currentValue} label={`Current ${label}`} />
      </Box>
      <Text fontSize="sm" width="40px">
        {displayValue}
      </Text>
    </HStack>
  );
};

const ScaleTicks: React.FC = () => {
  const ticks = [];
  for (let i = MIN_PH; i <= MAX_PH; i++) {
    const position = ((i - MIN_PH) / (MAX_PH - MIN_PH)) * 100;
    ticks.push(
      <Box key={i} position="absolute" left={`${position}%`}>
        <Box
          position="absolute"
          top="0"
          left="0"
          width="1px"
          height={TICK_HEIGHT}
          bg="gray.400"
          transform="translateX(-50%)"
        />
        <Text
          position="absolute"
          top={TICK_HEIGHT}
          left="0"
          fontSize="xs"
          color="gray.600"
          transform="translateX(-50%)"
        >
          {i}
        </Text>
      </Box>
    );
  }
  return <>{ticks}</>;
};

export const PhMeasurementsChart: React.FC<PhMeasurementsChartProps> = ({ analysis }) => {
  const optimalPosition = ((OPTIMAL_PH - MIN_PH) / (MAX_PH - MIN_PH)) * 100;

  // Check if we have historical data
  const hasHistory = "week1AverageUrinePh" in analysis;

  return (
    <Box borderWidth="1px" borderColor="brand.dark_green" borderRadius="lg" p={4}>
      <Text textStyle="sectionTitle">pH Measurements</Text>
      <Box>
        <Box p={6} borderRadius="xl">
          <Box position="relative">
            {/* Vertical optimal line that spans all rows */}
            <Box
              position="absolute"
              left={`calc(${LINE_WIDTH} * ${optimalPosition / 100} + ${LABEL_WIDTH} + ${LABEL_MARGIN})`}
              top="0"
              bottom="0"
              width="2px"
              bg={OPTIMAL_LINE_COLOR}
              opacity={0.6}
              transform="translateX(-50%)"
              zIndex={0}
            />
            <VStack spacing={4} align="stretch">
              <PhRow
                label="Saliva pH"
                currentValue={analysis.originalRequest.salivaPh}
                historicalValues={
                  hasHistory
                    ? [
                        (analysis as PhCalculationWithHistoryResponse).week2AverageSalivaPh,
                        (analysis as PhCalculationWithHistoryResponse).week1AverageSalivaPh
                      ]
                    : undefined
                }
              />
              <PhRow label="Weighted Avg pH" currentValue={analysis.weightedAveragePh} />
              <PhRow
                label="Urine pH"
                currentValue={analysis.originalRequest.urinePh}
                historicalValues={
                  hasHistory
                    ? [
                        (analysis as PhCalculationWithHistoryResponse).week2AverageUrinePh,
                        (analysis as PhCalculationWithHistoryResponse).week1AverageUrinePh
                      ]
                    : undefined
                }
              />
            </VStack>
            <Box position="relative" width={LINE_WIDTH} height="24px" ml={`calc(${LABEL_WIDTH} + ${LABEL_MARGIN})`}>
              <ScaleTicks />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
